
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../dashboard/components/DashboardLayout';
import UserList from './components/UserList';
import CreateUserForm from './components/CreateUserForm';
import UserDetail from './components/UserDetail';
import { supabase } from '../../lib/supabase';

interface User {
  id: string;
  username: string;
  display_name: string;
  full_name: string; // Thêm field này cho UserList
  email: string;
  phone?: string;
  address?: string;
  role: 'manager' | 'partner';
  commission_rate: number;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
}

export default function UsersPage() {
  const { user, userProfile } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'manager' | 'partner'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Kiểm tra quyền truy cập
  if (!user || userProfile?.role !== 'manager') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-lock-line text-4xl text-gray-400 mb-4"></i>
            <h2 className="text-xl font-semibold text-gray-600 mb-2">Không có quyền truy cập</h2>
            <p className="text-gray-500">Chỉ Manager mới có thể truy cập trang này.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Gọi Edge Function để lấy danh sách users
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-users-safe?action=list`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Không thể tải dữ liệu người dùng');
      }

      const { users: fetchedUsers } = await response.json();
      
      // Format users để có full_name field
      const formattedUsers = (fetchedUsers || []).map((user: any) => ({
        ...user,
        full_name: user.display_name || user.username || user.email?.split('@')[0] || 'User'
      }));
      
      setUsers(formattedUsers);
    } catch (error) {
      console.error('Lỗi khi tải dữ liệu người dùng:', error);
      alert('Không thể tải dữ liệu người dùng. Vui lòng thử lại!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (userData: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-users-safe?action=create`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Không thể tạo người dùng');
      }

      alert('Tạo người dùng thành công!');
      setShowCreateForm(false);
      fetchUsers(); // Reload danh sách
    } catch (error) {
      console.error('Lỗi khi tạo người dùng:', error);
      alert(`Lỗi: ${error.message}`);
    }
  };

  const handleUpdateUser = async (userId: string, userData: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-users-safe?action=update`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: userId, ...userData }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Không thể cập nhật người dùng');
      }

      alert('Cập nhật người dùng thành công!');
      setSelectedUser(null);
      setEditingUser(null);
      fetchUsers(); // Reload danh sách
    } catch (error) {
      console.error('Lỗi khi cập nhật người dùng:', error);
      alert(`Lỗi: ${error.message}`);
    }
  };

  const handleDeleteUser = async (userToDelete: User) => {
    if (!confirm('Bạn có chắc chắn muốn xóa người dùng này?')) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-users-safe?action=delete&id=${userToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Không thể xóa người dùng');
      }

      alert('Xóa người dùng thành công!');
      fetchUsers(); // Reload danh sách
    } catch (error) {
      console.error('Lỗi khi xóa người dùng:', error);
      alert(`Lỗi: ${error.message}`);
    }
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* User List với tất cả props cần thiết */}
        <UserList
          users={filteredUsers}
          onViewUser={setSelectedUser}
          onEditUser={setEditingUser}
          onDeleteUser={handleDeleteUser}
          onCreateUser={() => setShowCreateForm(true)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          currentUser={userProfile}
        />

        {/* Create User Modal */}
        {showCreateForm && (
          <CreateUserForm
            onSubmit={handleCreateUser}
            onCancel={() => setShowCreateForm(false)}
          />
        )}

        {/* User Detail Modal */}
        {selectedUser && (
          <UserDetail
            user={selectedUser}
            onUpdate={handleUpdateUser}
            onClose={() => setSelectedUser(null)}
          />
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <UserDetail
            user={editingUser}
            onUpdate={handleUpdateUser}
            onClose={() => setEditingUser(null)}
            isEditing={true}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

// Export User interface để các component khác sử dụng
export type { User };
